import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  planPriceApi,
  planPriceQueryKeys,
  type TenantPlanPrice,
} from "../../shared/api/platformBilling";
import { platformApi } from "../../shared/api/platform";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const VND = new Intl.NumberFormat("vi-VN");
const SELLABLE = ["BASIC", "PRO"];

function day(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString("vi-VN") : "—";
}

/**
 * Giá theo hợp đồng của từng phòng khám (#18990 P2.1).
 *
 * <p>Trước màn hình này, hai endpoint đã có nhưng chỉ gọi được bằng curl — nghĩa là muốn cho một
 * khách hàng mức giá đã hứa, kinh doanh phải nhờ kỹ thuật. Một quy trình như vậy thì hoặc là chậm,
 * hoặc là người ta bỏ qua và hứa miệng.
 *
 * <p>Giá cũ <b>không bị xoá</b>, chỉ bị đóng lại: một kỳ đã thu phải tra ngược được về mức giá có
 * hiệu lực lúc đó. Đó cũng là điều phép kiểm PRICE_MISMATCH dựa vào.
 */
export function PlatformPlanPricePage() {
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState<string>("");

  const tenants = useQuery({
    queryKey: ["platform", "tenants"],
    queryFn: () => platformApi.listTenants(),
  });

  const history = useQuery({
    queryKey: planPriceQueryKeys.history(tenantId),
    queryFn: () => planPriceApi.history(tenantId),
    enabled: tenantId !== "",
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-foreground">Giá theo hợp đồng</h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Mức giá riêng cho từng phòng khám. Không đặt gì ở đây thì phòng khám trả{" "}
          <b>giá niêm yết</b> của gói. Giá cũ không bị xoá, chỉ bị đóng lại — một kỳ đã thu phải
          tra ngược được về mức giá có hiệu lực lúc đó.
        </p>
      </div>

      <div className="max-w-md space-y-1">
        <Label htmlFor="tenant">Phòng khám</Label>
        <select
          id="tenant"
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
        >
          <option value="">— chọn phòng khám —</option>
          {(tenants.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.code})
            </option>
          ))}
        </select>
      </div>

      {tenantId !== "" && (
        <>
          <SetPriceForm
            tenantId={tenantId}
            onApplied={() =>
              qc.invalidateQueries({ queryKey: planPriceQueryKeys.history(tenantId) })
            }
          />
          <PriceHistory rows={history.data ?? []} loading={history.isLoading} />
        </>
      )}
    </div>
  );
}

function SetPriceForm({
  tenantId,
  onApplied,
}: {
  tenantId: string;
  onApplied: () => void;
}) {
  const [planCode, setPlanCode] = useState("BASIC");
  const [priceVnd, setPriceVnd] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [contractRef, setContractRef] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      planPriceApi.set({
        tenantId,
        planCode,
        priceVnd: Number(priceVnd),
        // Bỏ trống = có hiệu lực từ bây giờ. Không tự đặt một mốc quá khứ: lùi ngày hiệu lực sẽ
        // làm các kỳ ĐÃ THU trước đó bị chấm là sai giá.
        effectiveFrom: effectiveFrom === "" ? null : new Date(effectiveFrom).toISOString(),
        contractRef: contractRef.trim() === "" ? null : contractRef.trim(),
        source: "MANUAL",
      }),
    onSuccess: (r) => {
      setError(null);
      setNotice(
        r.applied
          ? `Đã đặt giá ${VND.format(Number(priceVnd))}đ cho ${planCode}.`
          : `Không áp dụng: ${r.reason ?? "phiên bản cũ hơn giá đang có"}.`
      );
      setPriceVnd("");
      onApplied();
    },
    onError: (e: unknown) => {
      setNotice(null);
      setError(apiError(e).detail);
    },
  });

  const valid = priceVnd.trim() !== "" && Number(priceVnd) > 0;

  return (
    <form
      className="space-y-3 rounded-lg border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="plan">Gói</Label>
          <select
            id="plan"
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            value={planCode}
            onChange={(e) => setPlanCode(e.target.value)}
          >
            {SELLABLE.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="price">Giá / tháng (đ)</Label>
          <Input
            id="price"
            inputMode="numeric"
            value={priceVnd}
            onChange={(e) => setPriceVnd(e.target.value.replace(/\D/g, ""))}
            placeholder="1200000"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="from">Hiệu lực từ</Label>
          <Input
            id="from"
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="ref">Số hợp đồng</Label>
          <Input
            id="ref"
            value={contractRef}
            onChange={(e) => setContractRef(e.target.value)}
            placeholder="HD-2026-001"
          />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Bỏ trống <b>Hiệu lực từ</b> nghĩa là áp dụng ngay. Số hợp đồng là thứ duy nhất truy ngược
        được một mức giá về nơi nó được duyệt — đừng bỏ trống nếu có.
      </p>

      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={!valid || save.isPending}>
          {save.isPending ? "Đang lưu…" : "Đặt giá"}
        </Button>
        {error && <span className="text-sm text-destructive">{error}</span>}
        {notice && <span className="text-sm text-emerald-700">{notice}</span>}
      </div>
    </form>
  );
}

function PriceHistory({ rows, loading }: { rows: TenantPlanPrice[]; loading: boolean }) {
  if (loading) {
    return <p className="text-sm text-muted-foreground">Đang tải…</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Phòng khám này chưa có giá riêng — đang trả giá niêm yết của gói.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[44rem] text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-right font-medium">Giá / tháng</th>
            <th className="px-3 py-2 text-left font-medium">Từ</th>
            <th className="px-3 py-2 text-left font-medium">Đến</th>
            <th className="px-3 py-2 text-left font-medium">Hợp đồng</th>
            <th className="px-3 py-2 text-left font-medium">Nguồn</th>
            <th className="px-3 py-2 text-right font-medium">Phiên bản</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="px-3 py-2 text-right tabular-nums font-medium">
                {VND.format(r.priceVnd)}đ
              </td>
              <td className="px-3 py-2">{day(r.effectiveFrom)}</td>
              <td className="px-3 py-2">
                {r.effectiveTo ? (
                  day(r.effectiveTo)
                ) : (
                  <span className="text-emerald-700">còn hiệu lực</span>
                )}
              </td>
              <td className="px-3 py-2">{r.contractRef ?? "—"}</td>
              <td className="px-3 py-2">{r.source}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.version}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
